import React from "react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoaderCommon, CommissionBanner, PointsBanner } from "../Common";
import { PromoTourModule } from "@module";
import {
    fetchHomePageSchema,
    fetchHomePageProductWithType,
    selectHomePageProduct,
    selectSearchCategories,
    useAppDispatch,
    useAppSelector,
    setChipType,
    setAccommodation,
    setTransport,
    fetchFavoriteTours,
    setActivity,
    extractCategoryDataFromProducts,
    fetchSearchFilters,
} from "@redux";
import styled from "styled-components";
import _ from "lodash";
import { breakpoint, theme } from "@utilities";
import { DiscountBanner } from "@components";
import { PATH_SEARCH_RESULT } from "@routes";
import { ICategories, IOption } from "@interfaces";
import { visibleChipTypes } from "./chipTypeVisibility";

export const ToursCategory = () => {
    const dispatch = useAppDispatch();
    const { homePageProducts, homePageSchema, status } = useAppSelector(selectHomePageProduct);
    const selectedChipType = useAppSelector((state) => state.homePageProduct.selectedChipType);
    const selectedLocations = useAppSelector((state) => state.locationSearch.selectedLocations);
    const authJWT = useAppSelector((state) => state.auth.jwt);
    const account = useAppSelector((state) => state.account.account);
    const metadata = useAppSelector((state) => state.account.metadata);
    const activeDiscount = useAppSelector((state) => state.checkout.activeDiscount);

    useEffect(() => {
        const shouldNotFetch = !_.isNil(authJWT) && !_.isNil(account);
        if (!shouldNotFetch) return;
        dispatch(fetchFavoriteTours());
    }, [dispatch, authJWT, account]);
    const searchCategories = useAppSelector(selectSearchCategories);
    const navigate = useNavigate();

    useEffect(() => {
        const shouldNotFetch = !_.isNil(authJWT) && !_.isNil(account);
        if (!shouldNotFetch) return;
        dispatch(fetchFavoriteTours());
    }, [dispatch, authJWT, account]);

    useEffect(() => {
        dispatch(fetchHomePageSchema());
    }, [dispatch]);

    const chipTypes = useMemo(
        () => visibleChipTypes(_.get(homePageSchema, "data", []).map((schema) => schema.type)),
        [homePageSchema],
    );

    useEffect(() => {
        if (!_.isEmpty(chipTypes)) {
            const initialTourType = _.first(chipTypes);
            dispatch(setChipType(initialTourType!));
        }
    }, [chipTypes]);

    // Fetch products when schema, chip, or locations change
    useEffect(() => {
        const hasRequiredData = homePageSchema && selectedChipType;
        if (!hasRequiredData) return;
        dispatch(fetchHomePageProductWithType());
    }, [dispatch, homePageSchema, selectedChipType, selectedLocations]);

    // Handle chip selection
    const handleTourTypeSelect = (selectedType: string) => {
        dispatch(setChipType(selectedType));
    };

    const getCategoryData = (schema: any, selectedChipType: string, labelName: string) => {
        const foundSchema = _.find(schema?.data, { type: selectedChipType });

        if (foundSchema) {
            const foundLabel = _.find(foundSchema.labels, { label: labelName });
            if (foundLabel?.categories?.length) {
                const categories = foundLabel.categories;
                return {
                    categories,
                    categoryIds: _.map(categories, (cat) => String(cat.category_id)),
                    categoryMap: _.reduce(
                        categories,
                        (acc, cat) => {
                            acc[cat.category_id] = cat.category;
                            return acc;
                        },
                        {} as Record<string, string>,
                    ),
                    categoryType: categories[0]?.category_type,
                };
            }
        }

        const extractedData = extractCategoryDataFromProducts(homePageProducts, labelName);
        if (!extractedData) return null;

        return {
            categories: extractedData.categories,
            categoryIds: extractedData.categoryIds,
            categoryMap: extractedData.categoryMap,
            categoryType: extractedData.categoryType,
        };
    };

    const filtersLoaded = useAppSelector((state) => (state.searchCategories as any).filtersLoaded);
    const filtersLoading = useAppSelector((state) => (state.searchCategories as any).filtersLoading);

    useEffect(() => {
        if (!filtersLoaded && !filtersLoading) {
            dispatch(fetchSearchFilters());
        }
    }, [dispatch, filtersLoaded, filtersLoading]);

    const handleExploreMoreClick = (labelName: string) => {
        const category = homePageProducts[labelName];
        const countryId = selectedLocations?.country?.id;

        if (selectedChipType.toLowerCase() === "destinations") {
            if (!category?.labelId || !countryId) {
                return;
            }

            dispatch(setActivity([]));
            dispatch(setAccommodation([]));
            dispatch(setTransport([]));

            const redirectPathName = `${PATH_SEARCH_RESULT}?regions=${category.labelId}&countries=${countryId}&destinationExplore=true`;
            navigate(redirectPathName);
            return;
        }

        if (!countryId) return;

        const categoryData = getCategoryData(homePageSchema, selectedChipType, labelName);

        if (!categoryData || _.isEmpty(categoryData.categories)) {
            dispatch(setActivity([]));
            dispatch(setAccommodation([]));
            dispatch(setTransport([]));
            const redirectPathName = `${PATH_SEARCH_RESULT}?query=${encodeURIComponent(labelName)}`;
            navigate(redirectPathName);
            return;
        }

        const categoriesByType = _.groupBy(categoryData.categories, "category_type");
        const categoryTypeKeys = _.keys(categoriesByType);

        if (_.isEmpty(categoryTypeKeys)) {
            dispatch(setActivity([]));
            dispatch(setAccommodation([]));
            dispatch(setTransport([]));
            const redirectPathName = `${PATH_SEARCH_RESULT}?query=${encodeURIComponent(labelName)}`;
            navigate(redirectPathName);
            return;
        }

        const primaryCategoryType = _.reduce(categoryTypeKeys, (a, b) =>
            _.size(categoriesByType[a]) >= _.size(categoriesByType[b]) ? a : b,
        ) as string;

        const categories = categoriesByType[primaryCategoryType];
        const categoryNames = _.map(categories, (cat) => cat.category?.toLowerCase());
        const fetchedCategories =
            searchCategories.categories[primaryCategoryType as keyof ICategories] || [];

        const matchedCategories: IOption[] = (fetchedCategories as any[])
            .filter(
                (item: any): item is IOption =>
                    item !== null &&
                    typeof item === "object" &&
                    "id" in item &&
                    "text" in item &&
                    (typeof item.id === "number" || typeof item.id === "string") &&
                    typeof item.text === "string",
            )
            .filter((item: IOption) => categoryNames.includes(item.text.toLowerCase()));

        dispatch(setActivity([]));
        dispatch(setAccommodation([]));
        dispatch(setTransport([]));

        if (matchedCategories.length > 0) {
            switch (primaryCategoryType) {
                case "activities":
                    dispatch(setActivity(matchedCategories));
                    break;
                case "accommodation":
                    dispatch(setAccommodation(matchedCategories));
                    break;
                case "transport":
                    dispatch(setTransport(matchedCategories));
                    break;
            }
        }

        const redirectPathName = matchedCategories.length > 0
            ? `${PATH_SEARCH_RESULT}?query=${encodeURIComponent(searchCategories.name ?? "")}`
            : `${PATH_SEARCH_RESULT}?query=${encodeURIComponent(labelName)}`;
        navigate(redirectPathName);
    };
    return (
        <>
            <ChipsContainer>
                {chipTypes.map((tourType: string) => (
                    <Chips
                        key={tourType}
                        $active={tourType === selectedChipType}
                        onClick={() => handleTourTypeSelect(tourType)}
                    >
                        {tourType}
                    </Chips>
                ))}
            </ChipsContainer>
            <div className="category">
                {status === "loading" ? (
                    <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
                ) : _.isEmpty(homePageProducts) ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        No {selectedChipType} available at the moment
                    </div>
                ) : (
                    _.entries(homePageProducts).map(([name, category], idx) => (
                        <React.Fragment key={`${name}-${idx}`}>
                            <CategoryHeaderRow>
                                <CategoryHeading>{name}</CategoryHeading>
                                {category.tours.length >= 4 && (
                                    <ExploreMoreBtn onClick={() => handleExploreMoreClick(name)}>
                                        Explore More →
                                    </ExploreMoreBtn>
                                )}
                            </CategoryHeaderRow>
                            <PromoTourModule data={category.tours} />
                            {idx === 0 &&
                                selectedChipType.trim().toLowerCase() === "experiences" && (
                                    <>
                                        {!account?.is_agent_integrated &&
                                            activeDiscount &&
                                            activeDiscount.discount > 0 && (
                                                <DiscountBanner
                                                    activeDiscountPercent={activeDiscount.discount}
                                                    learnMoreHref="/about/discounts"
                                                />
                                            )}

                                        {account?.is_agent_integrated &&
                                            account?.is_points_displayed &&
                                            metadata?.is_commission_agent && (
                                                <CommissionBanner learnMoreHref="/about/commission" />
                                            )}

                                        {account?.is_agent_integrated &&
                                            !metadata?.is_commission_agent && (
                                                <PointsBanner learnMoreHref="/about/rewards" />
                                            )}
                                    </>
                                )}
                        </React.Fragment>
                    ))
                )}
            </div>
        </>
    );
};

const ChipsContainer = styled.div`
    display: flex;
    gap: 10px;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    overflow-x: auto;
    margin: 15px auto;
    max-width: 100%;
    padding: 0 16px;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
        display: none;
    }
    scrollbar-width: none;

    ${breakpoint.breakTablet`
        margin-top: 70px;
        justify-content: center; 
    `}
`;

const Chips = styled.div<{ $active?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 40px;
    background: ${(props) =>
        props.$active
            ? `linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]})`
            : "#fff"};
    padding: 10px 20px;
    cursor: pointer;
    font-weight: ${(props) => (props.$active ? 500 : 400)};
    color: ${(props) => (props.$active ? theme.colors.button_text : "#3C3C3C")};
    flex: 0 0 auto;
    border: ${(props) => {
        return props.$active ? "0.5px solid #D6F0F1" : `1px solid ${theme.colors.border_chip}`;
    }};
    z-index: 1;
`;

const CategoryHeading = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    flex: 1;
    line-height: 1.4;
    ${breakpoint.breakMobileMedium`
            width: ${(props) => props.theme.widths.wide_700};
        `}

    ${breakpoint.breakTablet`
            font-size: 28px;   
        `}
    
    ${breakpoint.breakIpadPro`
            width: ${(props) => props.theme.widths.wide_850}; 
        `}
    
    ${breakpoint.breakLarge`
            width: ${(props) => props.theme.widths.wide_1110};
        `}
`;

const CategoryHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 4px;
    margin: 0 auto 24px;
    flex-wrap: wrap;
    row-gap: 12px;

    ${breakpoint.breakTablet`
        width: ${(props) => props.theme.widths.wide_700};
    `}
    ${breakpoint.breakIpadPro`
        width: ${(props) => props.theme.widths.wide_850};
    `}
    ${breakpoint.breakLarge`
        width: ${(props) => props.theme.widths.wide_1110};
    `}
    ${breakpoint.breakMobile`
        flex-wrap: wrap;
        row-gap: 12px;
        flex-direction: row;
        align-items: flex-start;
        padding-left: 12px;
    `}
`;

const ExploreMoreBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    font-weight: 500;
    color: ${theme.colors.black};
    border: none;
    border-radius: 48px;
    background: transparent;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    /* Colour rather than a tinted panel: the link sits on white beside a heading,
       and the previous pale wash barely registered as a hover at all. */
    &:hover {
        background: transparent;
        color: ${theme.colors.main};
    }

    ${breakpoint.breakMobile`
        padding-left: 0;
        align-self: flex-start;
        padding: 8px 12px;
    `}
`;
